import { createContext, useContext, useState, useEffect } from "react";

const BusinessContext = createContext();

const STORAGE_KEY = "selected_business";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const BusinessProvider = ({ children }) => {
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load selected business from localStorage on mount, then VALIDATE it
  // against the logged-in user's accessible businesses. On a shared browser,
  // localStorage can carry another user's selection (e.g. a super_admin picked
  // Digital Nexus Media, then a Growteq admin logged in) — without this check
  // the UI shows a business the current user has no access to. The backend
  // clamps data access regardless (__TENANT_CLAMP_v1__), but the UI must not
  // display a stale tenant either.
  useEffect(() => {
    let cancelled = false;
    const stored = localStorage.getItem(STORAGE_KEY);
    let parsed = null;
    if (stored) {
      try {
        parsed = JSON.parse(stored);
        setSelectedBusiness(parsed); // optimistic — validated just below
      } catch (e) {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
    setIsLoading(false);

    (async () => {
      try {
        const r = await fetch(`${BACKEND_URL}/api/auth/my-businesses`, { credentials: "include" });
        if (!r.ok || cancelled) return; // not logged in yet — nothing to validate
        const data = await r.json();
        const accessible = Array.isArray(data) ? data : (data.businesses || []);
        if (cancelled || accessible.length === 0) return;
        const storedId = parsed?.business_id;
        const stillValid = storedId && accessible.some(b => b.business_id === storedId);
        if (!stillValid) {
          // Stale or foreign selection — fall back to the user's first business
          const fallback = accessible[0];
          setSelectedBusiness(fallback);
          localStorage.setItem(STORAGE_KEY, JSON.stringify(fallback));
        }
      } catch (_) { /* offline / unauthenticated — leave as-is */ }
    })();

    return () => { cancelled = true; };
  }, []);

  // Save to localStorage when business changes
  const selectBusiness = (business) => {
    setSelectedBusiness(business);
    if (business) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(business));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  };

  // Clear selected business (on logout or when needed)
  const clearBusiness = () => {
    setSelectedBusiness(null);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <BusinessContext.Provider
      value={{
        selectedBusiness,
        selectBusiness,
        clearBusiness,
        isLoading,
        businessId: selectedBusiness?.business_id || null,
        // Alias for backward compatibility
        selectedBusinessId: selectedBusiness?.business_id || null,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};

export const useBusiness = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusiness must be used within a BusinessProvider");
  }
  return context;
};

export default BusinessContext;
